import { type Component, createSignal, onMount, Show, For } from 'solid-js'
import { Icon } from '@iconify-icon/solid'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import DateTimeInput from '../../../components/shared/components/DateTimeInput'

dayjs.extend(utc)
dayjs.extend(timezone)
import { labelClass, errorClass } from './form-styles'
import type { EventFormData } from '../domain/event-form.schema'
import type { PresaleCodePoolWithEvents } from '../data/api/admin-events.api-datasource'

interface PresaleSectionProps {
  readonly form: EventFormData
  readonly setField: <K extends keyof EventFormData>(key: K, value: EventFormData[K]) => void
  readonly inputCls: (field: string) => string
  readonly fieldError: (field: string) => string | undefined
  readonly getPresaleCodePools: () => Promise<PresaleCodePoolWithEvents[]>
  readonly unassignPoolFromEvent?: (poolId: number, eventId: number) => Promise<void>
  readonly eventId?: number
  readonly onLinkedPoolsFound?: () => void
}

const PresaleSection: Component<PresaleSectionProps> = (props) => {
  const [linkedPools, setLinkedPools] = createSignal<PresaleCodePoolWithEvents[]>([])

  const fillDatesFromPool = (pool: PresaleCodePoolWithEvents): void => {
    if (props.form.presaleStart && props.form.presaleEnd) return
    const tz = props.form.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
    const utcToLocal = (utcStr: string | null): string =>
      utcStr ? dayjs.utc(utcStr).tz(tz).format('YYYY-MM-DDTHH:mm') : ''
    if (pool.presale_start && !props.form.presaleStart) props.setField('presaleStart', utcToLocal(pool.presale_start))
    if (pool.presale_end && !props.form.presaleEnd) props.setField('presaleEnd', utcToLocal(pool.presale_end))
  }

  // In edit mode, fetch pools to find which ones are linked to this event
  onMount(async () => {
    if (!props.eventId) return
    try {
      const pools = await props.getPresaleCodePools()
      const linked = pools.filter((p) => p.eventIds.includes(props.eventId!))
      setLinkedPools(linked)
      if (linked.length > 0) {
        props.onLinkedPoolsFound?.()
        fillDatesFromPool(linked[0])
      }
    } catch {
      // silently fail — pools just won't show
    }
  })

  const isLocked = (): boolean => linkedPools().length > 0

  return (
    <>
      {/* Excerpt */}
      <p class="text-sm text-gray-500">
        Set a window and a static presale code to grant early access. If a code pool is linked to this event, those
        codes take precedence and the static configuration is locked.
      </p>

      {/* Linked pool callout — shown at the top so the lock state is the first thing the user sees */}
      <Show when={isLocked()}>
        <div class="flex flex-col gap-2">
          <For each={linkedPools()}>
            {(pool) => {
              const [unlinking, setUnlinking] = createSignal(false)
              return (
                <div class="p-3 bg-primary-50 border border-primary-100 rounded-lg flex items-start gap-3">
                  <div class="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                    <Icon icon="heroicons:ticket" class="text-[1rem] text-primary-600" />
                  </div>
                  <div class="flex-1 min-w-0 flex flex-col gap-0.5">
                    <span class="text-sm font-medium text-gray-900">{pool.name}</span>
                    <span class="text-xs text-gray-600">
                      A code pool is linked to this event. Unlink it to set custom dates and a static code.
                    </span>
                  </div>
                  <Show when={props.unassignPoolFromEvent && props.eventId}>
                    <button
                      type="button"
                      class="w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors duration-150 shrink-0"
                      classList={{ 'opacity-50 pointer-events-none': unlinking() }}
                      title="Unlink pool from this event"
                      onClick={async () => {
                        setUnlinking(true)
                        try {
                          await props.unassignPoolFromEvent!(pool.id, props.eventId!)
                          setLinkedPools((prev) => prev.filter((p) => p.id !== pool.id))
                        } catch {
                          setUnlinking(false)
                        }
                      }}
                    >
                      <Icon icon="heroicons:link-slash" class="text-[1rem]" />
                    </button>
                  </Show>
                </div>
              )
            }}
          </For>
        </div>
      </Show>

      {/* Static presale config — dates + code, locked while a pool is linked */}
      <div class="flex flex-col gap-4">
        <div class="flex gap-4">
          <div class="flex-1 flex flex-col gap-2.5">
            <label class={labelClass}>Presale start</label>
            <DateTimeInput
              value={props.form.presaleStart}
              onValueChange={(v) => props.setField('presaleStart', v ?? '')}
              mode="datetime"
              placeholder="Select date & time"
              disabled={isLocked()}
            />
          </div>
          <div class="flex-1 flex flex-col gap-2.5">
            <label class={labelClass}>Presale end</label>
            <DateTimeInput
              value={props.form.presaleEnd}
              onValueChange={(v) => props.setField('presaleEnd', v ?? '')}
              mode="datetime"
              placeholder="Select date & time"
              disabled={isLocked()}
            />
          </div>
        </div>

        <div class="flex flex-col gap-2.5" data-field="presaleCode">
          <label class={labelClass}>Presale code</label>
          <input
            type="text"
            class={props.inputCls('presaleCode')}
            classList={{ 'opacity-60 cursor-not-allowed bg-gray-50': isLocked() }}
            value={props.form.presaleCode}
            onInput={(e) => props.setField('presaleCode', e.currentTarget.value)}
            placeholder="Code to access early ticket sales"
            disabled={isLocked()}
          />
          <Show when={!isLocked() && props.fieldError('presaleCode')}>
            <p class={errorClass}>{props.fieldError('presaleCode')}</p>
          </Show>
        </div>
      </div>
    </>
  )
}

export default PresaleSection
